import {
  RadioGroup,
  type RadioGroupOption,
} from "@circleco/compass/components/RadioGroup";
import { Typography } from "@circleco/compass/components/Typography";
import { useEffect, useRef } from "react";

import { t } from "@/i18n-js/instance";
import type { CheckoutSessionPaywall } from "@/react/types/Paywalls/Checkout/Session";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

import { upgradeCouponBadge } from "./helpers/upgradeCouponBadge";
import { upgradeSourceCycleAmount } from "./helpers/upgradePriceComparison";
import { UpgradeCurrentPlanCard } from "./UpgradeCurrentPlanCard";
import {
  UpgradeFigureSkeleton,
  UpgradeOptionsSkeleton,
} from "./UpgradeLoadingSkeletons";
import { UpgradePriceCallout } from "./UpgradePriceCallout";
import {
  UpgradePriceOptionLabel,
  UpgradePriceOptionSourceCycle,
  UpgradePriceOptionTerm,
  UpgradeSinglePriceTerm,
  UpgradeSinglePriceDescription,
} from "./UpgradePriceOptionContent";
import { UpgradePriceBadges } from "./UpgradeTargetTrialBadge";

const i18nRoot = "paywall_checkout.offer_page.upgrade";

interface UpgradePlanCardsProps {
  isDisabled?: boolean;
  /** Nothing has loaded yet: no preview in hand and a request in flight. */
  isLoading: boolean;
  /** A new price was selected; the previous preview is still on screen. */
  isRefreshing: boolean;
  onPriceChange: (priceId: number) => void;
  paywall: CheckoutSessionPaywall;
  preview?: UpgradePreview;
  pricePreviews?: UpgradePreview["price_options"];
  selectedPriceId: string;
  showPriceSelector: boolean;
}

export const UpgradePlanCards = ({
  isDisabled = false,
  isLoading,
  isRefreshing,
  onPriceChange,
  paywall,
  preview,
  pricePreviews = [],
  selectedPriceId,
  showPriceSelector,
}: UpgradePlanCardsProps) => {
  const sourceIntervalType = preview?.source.interval_type;
  const couponBadge = upgradeCouponBadge(
    preview,
    selectedPriceId,
    isRefreshing,
  );
  const previewedPriceOption = (priceId: number) => {
    const priceOption = preview?.price_options.find(
      option => option.paywall_price_id === priceId,
    );
    return priceOption &&
      !isRefreshing &&
      preview?.selected_paywall_price_id === priceId &&
      selectedPriceId === String(priceId)
      ? priceOption
      : (pricePreviews.find(option => option.paywall_price_id === priceId) ??
          (priceOption && {
            ...priceOption,
            coupon_term_amount: null,
            coupon_source_cycle_amount: null,
          }));
  };
  const options: RadioGroupOption[] = paywall.prices.map(price => {
    const currentPreviewOption = previewedPriceOption(price.id);
    if (!currentPreviewOption || !sourceIntervalType) {
      return {
        value: String(price.id),
        label: (
          <Typography variant="body-md">
            {price.title || paywall.display_name}
          </Typography>
        ),
        description: price.display_interval,
        disabled: isRefreshing || isDisabled,
      };
    }

    return {
      value: String(price.id),
      label: (
        <UpgradePriceOptionLabel
          priceId={price.id}
          priceTitle={price.title}
          priceOption={currentPreviewOption}
        />
      ),
      description: (
        <UpgradePriceOptionTerm
          isSelected={selectedPriceId === String(price.id)}
          priceOption={currentPreviewOption}
        />
      ),
      badge: (
        <UpgradePriceOptionSourceCycle
          priceOption={currentPreviewOption}
          sourceIntervalType={sourceIntervalType}
        />
      ),
      disabled: isRefreshing || isDisabled,
    };
  });
  const optionsRef = useRef<HTMLDivElement>(null);
  const shouldRestoreFocusRef = useRef(false);
  useEffect(() => {
    if (isRefreshing || !shouldRestoreFocusRef.current) return;
    shouldRestoreFocusRef.current = false;
    optionsRef.current
      ?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')
      ?.focus();
  }, [isRefreshing]);
  const isAwaitingFirstPreview = isLoading && !preview;
  const defaultPriceOption = preview
    ? previewedPriceOption(preview.default_paywall_price_id)
    : undefined;
  const selectedSinglePriceOption = preview?.price_options[0];
  const singlePriceOption =
    selectedSinglePriceOption &&
    !isRefreshing &&
    preview?.selected_paywall_price_id === Number(selectedPriceId)
      ? selectedSinglePriceOption
      : selectedSinglePriceOption && {
          ...selectedSinglePriceOption,
          coupon_term_amount: null,
          coupon_source_cycle_amount: null,
        };
  const headlinePriceOption = showPriceSelector
    ? defaultPriceOption
    : singlePriceOption;
  const headlineAmount =
    headlinePriceOption && sourceIntervalType
      ? upgradeSourceCycleAmount(headlinePriceOption, sourceIntervalType)
      : undefined;
  const selectedPriceOption = preview?.price_options.find(
    option => option.paywall_price_id === Number(selectedPriceId),
  );
  const continuingTrialDays =
    preview?.trial.disposition === "preserved" ||
    preview?.trial.disposition === "carried_forward"
      ? preview.source.trial_days
      : undefined;
  const selectedTrialDays =
    !isRefreshing &&
    preview?.selected_paywall_price_id === Number(selectedPriceId)
      ? (continuingTrialDays ?? selectedPriceOption?.target_trial_days)
      : undefined;
  // One price on the same billing cycle as the current plan is a like-for-like
  // comparison: the term price (with any discount) sits on the right of the
  // plan name, and the billing label would only repeat the unit beside it.
  const isSingleSameCycle =
    !showPriceSelector &&
    Boolean(preview && singlePriceOption) &&
    preview?.source.interval_type === singlePriceOption?.interval_type;

  return (
    <div
      className="flex w-full max-w-xl flex-col"
      data-testid="upgrade-plan-cards"
    >
      <UpgradeCurrentPlanCard
        isLoading={isAwaitingFirstPreview}
        showBilledCadence={!isSingleSameCycle}
        source={preview?.source}
      />

      <section
        className="-mx-2 rounded-xl border border-primary bg-primary p-4 shadow-sm"
        data-testid="upgrade-switch-to-card"
      >
        <div className="flex items-center justify-between gap-3">
          <Typography variant="label-sm" color="secondary">
            {t([i18nRoot, "switch_to"])}
          </Typography>
          <UpgradePriceBadges
            couponLabel={couponBadge?.label}
            trialDays={selectedTrialDays}
            isRefreshing={isRefreshing}
          />
        </div>
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-1">
            <Typography variant="heading-md">
              {preview?.labels.selected_plan || paywall.display_name}
            </Typography>
            {!showPriceSelector && singlePriceOption && (
              <UpgradePriceCallout
                priceId={singlePriceOption.paywall_price_id}
                showSeparator
              />
            )}
          </div>
          {isAwaitingFirstPreview && (
            <UpgradeFigureSkeleton className="h-5 w-24" />
          )}
          {isSingleSameCycle && singlePriceOption && (
            <UpgradeSinglePriceTerm priceOption={singlePriceOption} />
          )}
          {!isSingleSameCycle && headlineAmount && (
            <Typography
              className="inline-flex shrink-0 items-baseline gap-1"
              variant="body-md"
              data-testid="upgrade-headline-source-cycle-amount"
            >
              {preview && preview.price_options.length > 1 && (
                <span className="text-light">
                  {t([i18nRoot, "from_label"])}
                </span>
              )}
              <strong>{headlineAmount}</strong>
              <span className="text-light">
                {sourceIntervalType &&
                  t([i18nRoot, "term_units", sourceIntervalType])}
              </span>
            </Typography>
          )}
        </div>
        {isAwaitingFirstPreview && (
          <div className="mt-4">
            {showPriceSelector ? (
              <UpgradeOptionsSkeleton count={paywall.prices.length} />
            ) : (
              <UpgradeFigureSkeleton className="w-48" />
            )}
          </div>
        )}
        {!isAwaitingFirstPreview && showPriceSelector && (
          <div
            className="mt-4"
            data-testid="upgrade-price-options"
            ref={optionsRef}
          >
            <RadioGroup
              variant="option-cards"
              optionCardColumns={1}
              legend={t([i18nRoot, "select_price"])}
              legendProps={{ className: "sr-only" }}
              optionCardShowControl
              options={options}
              value={selectedPriceId}
              onValueChange={value => {
                shouldRestoreFocusRef.current = true;
                onPriceChange(Number(value));
              }}
            />
          </div>
        )}
        {!showPriceSelector && singlePriceOption && !isSingleSameCycle && (
          <UpgradeSinglePriceDescription priceOption={singlePriceOption} />
        )}
      </section>
    </div>
  );
};
